// pages/activities/soft-skills/index.tsx
import {
  createSoftProgramColumns,
  SoftProgramActionType,
} from "@/colDef/softProgramColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import Search from "@/components/Search";
import SoftProgramDialog from "@/components/formDialog/SoftProgramDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteSoftProgramMutation,
  useSoftProgramsQuery,
} from "@/hooks/api/softProgram.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import {
  SoftProgramParamsType,
  SoftProgramStatus,
  SoftProgramType,
} from "@/services/softProgram.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const SoftSkillsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<SoftProgramStatus | "all">(
    "all"
  );

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const programDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    programId: "",
    programData: null as SoftProgramType | null,
  });

  const filters: SoftProgramParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    }),
    [debouncedSearch, statusFilter, page, limit]
  );

  const { data, isLoading, isError, refetch } = useSoftProgramsQuery(filters);
  const deleteMutation = useDeleteSoftProgramMutation(filters);

  const programs = data?.programs ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: SoftProgramActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const programData = programs.find((p) => p._id === payload.id) ?? null;
        programDialog.open({
          mode: "edit",
          programId: payload.id,
          programData,
        });
      }
    },
    [deleteDialog, programDialog, programs]
  );

  const columns = useMemo(
    () => createSoftProgramColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load soft skills programs."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Soft Skills Programs</h1>
        <Button
          onClick={() =>
            programDialog.open({
              mode: "create",
              programId: "",
              programData: null,
            })
          }
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search + Status Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as typeof statusFilter);
            resetPage();
          }}
        >
          <SelectTrigger className="w-36 gap-2">
            <Filter size={14} className="text-gray-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="ongoing">Ongoing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<SoftProgramType, any>
              columns={columns}
              data={programs}
              onRowClick={(row) =>
                navigate(`/activities/soft-skills/${row._id}`)
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={programs.length}
        onPageChange={setPage}
      />

      {/* Create / Edit Dialog */}
      <SoftProgramDialog
        mode={programDialog.state.mode}
        isOpen={programDialog.state.isOpen}
        onOpenChange={(open) => !open && programDialog.close()}
        filters={filters}
        programId={programDialog.state.programId}
        programData={programDialog.state.programData}
        onCancel={programDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default SoftSkillsPage;
